/* ==========================================================================
   Sura hero slider
   Scroll-snap does the moving; JS only drives autoplay, arrows and dots.
   ========================================================================== */

class SuraHero extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
    this.prevBtn = this.querySelector('[data-hero-prev]');
    this.nextBtn = this.querySelector('[data-hero-next]');

    if (!this.track || this.slides.length === 0) return;

    this.current = 0;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.watchActiveSlide();
    this.bindControls();
    this.bindEditorEvents();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.observer) this.observer.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  /* ----------------------------------------------------------- Active slide */

  watchActiveSlide() {
    // Whichever slide occupies most of the viewport is the active one.
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number(entry.target.dataset.index);
          if (Number.isNaN(index)) return;
          this.current = index;
          this.syncControls();
        });
      },
      { root: this.track, threshold: 0.6 }
    );

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  syncControls() {
    this.dots.forEach((dot, i) => {
      const active = i === this.current;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', String(active));
    });

    // Slides outside the viewport shouldn't be reachable by tab.
    this.slides.forEach((slide, i) => {
      slide.toggleAttribute('inert', i !== this.current);
    });
  }

  /* -------------------------------------------------------------- Controls */

  bindControls() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goTo(this.current - 1, true));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goTo(this.current + 1, true));

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(Number(dot.dataset.index), true));
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.current + 1, true);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.current - 1, true);
      }
    });

    // Pause while the visitor is engaging with it.
    ['mouseenter', 'focusin', 'touchstart'].forEach((evt) =>
      this.addEventListener(evt, () => this.stopAutoplay(), { passive: true })
    );
    ['mouseleave', 'focusout'].forEach((evt) =>
      this.addEventListener(evt, () => this.startAutoplay())
    );

    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  onVisibilityChange = () => {
    document.hidden ? this.stopAutoplay() : this.startAutoplay();
  };

  goTo(index, userInitiated = false) {
    const total = this.slides.length;
    const target = ((index % total) + total) % total; // wraps both directions
    const slide = this.slides[target];
    if (!slide) return;

    this.track.scrollTo({
      left: slide.offsetLeft - this.track.offsetLeft,
      behavior: this.reducedMotion ? 'auto' : 'smooth',
    });

    this.current = target;
    this.syncControls();

    // A deliberate click restarts the timer rather than cutting it short.
    if (userInitiated) this.startAutoplay();
  }

  /* -------------------------------------------------------------- Autoplay */

  startAutoplay() {
    this.stopAutoplay();
    if (this.dataset.autoplay !== 'true') return;
    if (this.reducedMotion) return;
    if (this.slides.length < 2) return;

    const seconds = Number(this.dataset.autoplaySpeed) || 6;
    this.timer = setInterval(() => this.goTo(this.current + 1), seconds * 1000);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /* --------------------------------------------------------- Theme editor  */

  bindEditorEvents() {
    if (!Shopify.designMode) return;

    // Jump to a slide when the merchant selects its block, and hold there.
    document.addEventListener('shopify:block:select', (event) => {
      if (!this.contains(event.target)) return;
      const index = Number(event.target.dataset.index);
      if (Number.isNaN(index)) return;
      this.stopAutoplay();
      this.goTo(index);
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      if (!this.contains(event.target)) return;
      this.startAutoplay();
    });
  }
}

if (!customElements.get('sura-hero')) {
  customElements.define('sura-hero', SuraHero);
}
